import type { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import React, { useId, useMemo } from 'react';

import { Button } from '@/components/core/button';
import { Label } from '@/components/core/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';
import { Skeleton } from '@/components/core/skeleton';
import { cn, formatNumber } from '@/shared/helpers';

type DataTablePaginationProps<TData> = React.ComponentProps<'div'> & {
  table: Table<TData>;
  pageSizeOptions?: number[];
};

function TotalCount<TData>({ table }: { table: Table<TData> }) {
  const totalRows = table.getRowCount();
  const { isLoading } = table.options.meta ?? {};
  const { rowSelection } = table.getState();
  const totalSelected = useMemo(
    () => Object.values(rowSelection).filter(Boolean).length,
    [rowSelection],
  );
  const showSkeleton = isLoading;

  if (table.options.enableRowSelection) {
    if (showSkeleton) {
      return <Skeleton className="h-4 w-40" />;
    }
    return `${formatNumber(totalSelected)} of ${formatNumber(totalRows)} row${totalRows === 1 ? '' : 's'} selected`;
  }

  if (showSkeleton) {
    return <Skeleton className="h-4 w-28" />;
  }
  return `${formatNumber(totalRows)} total row${totalRows === 1 ? '' : 's'}`;
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 25, 50, 100],
  className,
  ...props
}: DataTablePaginationProps<TData>) {
  const pageSizeId = useId();
  const { isLoading } = table.options.meta ?? {};
  const showSkeleton = isLoading;
  return (
    <div
      data-slot="pagination"
      className={cn(
        'flex min-h-14 w-full items-center justify-between tabular-nums sm:gap-8',
        className,
      )}
      {...props}
    >
      <div className="hidden flex-1 text-sm whitespace-nowrap text-muted-foreground sm:block">
        <TotalCount table={table} />
      </div>
      <div className="flex w-full items-center justify-between gap-4 sm:w-auto">
        <div className="hidden items-center space-x-2 sm:flex">
          <Label
            htmlFor={pageSizeId}
            className="font-normal whitespace-nowrap text-muted-foreground"
          >
            Rows per page
          </Label>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger id={pageSizeId}>
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
          {showSkeleton ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            `Page ${formatNumber(table.getState().pagination.pageIndex + 1)} of ${formatNumber(Math.max(table.getPageCount(), 1))}`
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            aria-label="Go to first page"
            variant="outline"
            size="icon"
            className="hidden lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft />
          </Button>
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight />
          </Button>
          <Button
            aria-label="Go to last page"
            variant="outline"
            size="icon"
            className="hidden lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
